import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';

// Scanner Register — who is carrying which AP handheld, the spare pool, and what's
// away for repair. Replaces the "Scanner Information.xlsx" spreadsheet, and is the
// source of truth for the asset number shown on the Documents page (every custody
// change here writes the driver's DMS `scanner_asset` record).
//
// Tabs:
//  • Drivers   — one row per driver: what they hold, since when, what looks wrong
//  • Scanners  — the whole register, any status, add / edit / act
//  • Spares & Repairs — the pool, and everything away being fixed
//  • History   — the custody log: every assign, swap, return, retire
//  • Import    — reconcile a spreadsheet against the register before committing it
const API = '/.netlify/functions/scanners-sb';

const post = async (body) => {
  const res = await fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const text = await res.text();
  let j;
  try { j = JSON.parse(text); } catch { throw new Error(`The server took too long to reply (HTTP ${res.status}).`); }
  if (!j.success) throw new Error(j.error || 'Request failed');
  return j;
};

// ── shared styles (same idiom as the other admin pages) ───────────────────────
const cell = { padding: '7px 10px', borderBottom: '1px solid var(--hp-border)', fontSize: '0.85rem', textAlign: 'left', verticalAlign: 'top' };
const th = { ...cell, color: 'var(--hp-dim)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.04em', whiteSpace: 'nowrap' };
const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.82rem' };
const field = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.9rem' };
const stat = { flex: 1, minWidth: 120, padding: '12px 14px', borderRadius: 10, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)' };
const btn = { padding: '7px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)', fontSize: '0.85rem', cursor: 'pointer' };
const btnPrimary = { ...btn, background: 'var(--hp-accent, #2563eb)', color: '#fff', border: 'none' };
const btnSmall = { ...btn, padding: '4px 9px', fontSize: '0.78rem' };
const card = { border: '1px solid var(--hp-border)', borderRadius: 10, background: 'var(--hp-surface)', padding: 14 };

const STATUS_STYLE = {
  assigned: { bg: 'rgba(34,197,94,0.14)', fg: '#16a34a', label: 'With driver' },
  spare: { bg: 'rgba(59,130,246,0.14)', fg: '#2563eb', label: 'Spare' },
  repair: { bg: 'rgba(245,158,11,0.16)', fg: '#b45309', label: 'At repair' },
  retired: { bg: 'rgba(148,163,184,0.18)', fg: '#64748b', label: 'Retired' },
};
function Pill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.retired;
  return <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600, background: s.bg, color: s.fg, whiteSpace: 'nowrap' }}>{s.label}</span>;
}
function Warn({ children, tone = 'warn' }) {
  const c = tone === 'bad' ? { bg: 'rgba(239,68,68,0.12)', fg: '#dc2626' } : { bg: 'rgba(245,158,11,0.16)', fg: '#b45309' };
  return <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', background: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>{children}</span>;
}
// A Christmas loan is still AP's device — it gets its own mark everywhere it is
// listed so nobody reads it as one of ours and lets it sit out the year.
function PeakTag() {
  return <span style={{ padding: '2px 7px', borderRadius: 6, fontSize: '0.68rem', fontWeight: 600, background: 'rgba(16,185,129,0.14)', color: '#047857', whiteSpace: 'nowrap' }}
    title="On loan from AP for peak — due back">PEAK</span>;
}
function StatCard({ label, value, title }) {
  return (
    <div style={stat} title={title}>
      <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{value}</div>
    </div>
  );
}

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtDate = (iso) => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : (iso || '—'));
const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const daysSince = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}/.test(iso || '')) return null;
  const then = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return Math.round((new Date(`${todayISO()}T00:00:00`) - then) / 86400000);
};

const TABS = [
  { key: 'drivers', label: 'Drivers' },
  { key: 'register', label: 'Scanners' },
  { key: 'pool', label: 'Spares & Repairs' },
  { key: 'audit', label: 'Asset Audit' },
  { key: 'history', label: 'History' },
  { key: 'import', label: 'Import' },
];

// ── page ──────────────────────────────────────────────────────────────────────
export default function ScannersPage() {
  const [tab, setTab] = useState('drivers');
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState('');

  const load = useCallback(() => {
    post({ action: 'load' })
      .then((j) => { setData(j); setLoadErr(''); })
      .catch((e) => setLoadErr(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Every mutation goes through here so the register, the flash line and the busy
  // state stay in step, and a DMS write-back is always reported back to the operator.
  const act = useCallback(async (body, describe) => {
    setBusy(true); setFlash('');
    try {
      const j = await post(body);
      const dms = (j.dms || []).filter((d) => !d.error);
      const dmsErrs = (j.dms || []).filter((d) => d.error);
      let msg = describe || 'Saved.';
      if (dms.length) msg += ` Documents page updated for ${dms.map((d) => d.login).join(', ')}.`;
      if (dmsErrs.length) msg += ` ⚠ Documents update failed for ${dmsErrs.map((d) => d.login).join(', ')}.`;
      setFlash(msg);
      await post({ action: 'load' }).then(setData);
      return j;
    } catch (e) {
      setFlash(`⚠ ${e.message}`);
      throw e;
    } finally { setBusy(false); }
  }, []);

  const scanners = data ? data.scanners : [];
  const drivers = data ? data.drivers : [];
  const dms = data ? data.dms : {};

  const byLogin = useMemo(() => {
    const m = new Map();
    scanners.filter((s) => s.status === 'assigned' && s.holder).forEach((s) => {
      if (!m.has(s.holder)) m.set(s.holder, []);
      m.get(s.holder).push(s);
    });
    return m;
  }, [scanners]);
  const driverName = useCallback((login) => {
    const d = drivers.find((x) => x.login === login);
    return d ? (d.name || d.login) : login;
  }, [drivers]);

  const counts = useMemo(() => ({
    assigned: scanners.filter((s) => s.status === 'assigned').length,
    spare: scanners.filter((s) => s.status === 'spare').length,
    repair: scanners.filter((s) => s.status === 'repair').length,
    retired: scanners.filter((s) => s.status === 'retired').length,
    peak: scanners.filter((s) => s.fleet === 'peak' && s.status !== 'retired').length,
  }), [scanners]);

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1200 }}>
      <h1 style={{ margin: '0 0 4px', fontSize: '1.4rem' }}>Scanners</h1>
      <p style={{ margin: '0 0 16px', color: 'var(--hp-dim)', fontSize: '0.9rem' }}>
        Who is carrying which AP handheld, the spare pool, and anything away for repair. Every change here also
        updates that driver&rsquo;s <strong>Scanner Asset Numbers</strong> record on the Documents page, so the two never drift.
      </p>

      {loadErr && <div style={{ ...card, borderColor: '#dc2626', color: '#dc2626', marginBottom: 16 }}>{loadErr}</div>}
      {flash && (
        <div style={{ ...card, marginBottom: 16, borderColor: flash.startsWith('⚠') ? '#dc2626' : 'var(--hp-border)', fontSize: '0.88rem' }}>
          {flash}
          <button style={{ ...btnSmall, marginLeft: 12 }} onClick={() => setFlash('')}>Dismiss</button>
        </div>
      )}

      {data && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <StatCard label="With drivers" value={counts.assigned} />
          <StatCard label="Spare" value={counts.spare} />
          <StatCard label="At repair" value={counts.repair} />
          <StatCard label="Retired" value={counts.retired} title="Handed back to AP or written off — kept for the history" />
          {counts.peak > 0 && <StatCard label="Peak loans" value={counts.peak} title="Christmas scanners on loan from AP — due back after peak" />}
          <StatCard label="Drivers" value={drivers.filter((d) => d.active && !d.sorterOnly).length}
            title="Active drivers who carry a handheld — night sorters are not counted" />
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18, borderBottom: '1px solid var(--hp-border)' }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.9rem',
            color: tab === t.key ? 'var(--hp-text)' : 'var(--hp-dim)',
            borderBottom: tab === t.key ? '2px solid var(--hp-accent, #2563eb)' : '2px solid transparent',
            fontWeight: tab === t.key ? 600 : 400,
          }}>{t.label}</button>
        ))}
      </div>

      {!data && !loadErr && <p style={{ color: 'var(--hp-dim)' }}>Loading the register…</p>}

      {data && tab === 'drivers' && (
        <DriversTab drivers={drivers} scanners={scanners} byLogin={byLogin} dms={dms} seeded={data.seeded}
          act={act} busy={busy} driverName={driverName} onImport={() => setTab('import')} />
      )}
      {data && tab === 'register' && (
        <RegisterTab scanners={scanners} drivers={drivers} act={act} busy={busy} driverName={driverName} />
      )}
      {data && tab === 'pool' && (
        <PoolTab scanners={scanners} drivers={drivers} act={act} busy={busy} />
      )}
      {data && tab === 'audit' && (
        <AuditTab data={data} act={act} busy={busy} />
      )}
      {data && tab === 'history' && (
        <HistoryTab moves={data.moves} driverName={driverName} />
      )}
      {data && tab === 'import' && (
        <ImportTab data={data} act={act} busy={busy} onDone={load} />
      )}

      {data && data.updatedAt && (
        <p style={{ marginTop: 24, fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
          Register last changed {fmtWhen(data.updatedAt)}{data.updatedBy ? ` by ${data.updatedBy}` : ''}.
        </p>
      )}
    </div>
  );
}

// ── Drivers ───────────────────────────────────────────────────────────────────
// The answer to "who has which scanner", read down the driver list rather than the
// scanner list — the way the question actually gets asked at the depot.
function DriversTab({ drivers, scanners, byLogin, dms, seeded, act, busy, driverName, onImport }) {
  const [showInactive, setShowInactive] = useState(false);
  const [q, setQ] = useState('');
  const [swapOpen, setSwapOpen] = useState(false);
  const [assignFor, setAssignFor] = useState(null);   // login awaiting a scanner

  const rows = drivers
    .filter((d) => showInactive || d.active)
    // Night sorters with no round carry no handheld — they are not a gap in the
    // register, so they never appear in it.
    .filter((d) => !d.sorterOnly)
    .filter((d) => {
      if (!q.trim()) return true;
      const held = (byLogin.get(d.login) || []).map((s) => s.asset).join(' ');
      return `${d.name} ${d.login} ${held}`.toLowerCase().includes(q.trim().toLowerCase());
    });

  const noScanner = drivers.filter((d) => d.active && !d.sorterOnly && !(byLogin.get(d.login) || []).length);
  const doubled = drivers.filter((d) => (byLogin.get(d.login) || []).length > 1);
  // The Documents page can still be edited by hand; a mismatch means someone typed a
  // number there instead of moving the scanner here. Before the register is seeded
  // EVERY Documents record differs from it, which is not news — so a driver the
  // register knows nothing about only counts once the register has been loaded.
  const isMismatch = (d) => {
    const held = (byLogin.get(d.login) || []).map((s) => s.asset).sort().join(', ');
    const rec = (dms[d.login] || '').trim();
    if (!rec && !held) return false;
    if (!held) return seeded && !!rec;
    return rec !== held;
  };
  const mismatched = drivers.filter((d) => d.active && !d.sorterOnly && isMismatch(d));

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <input style={{ ...field, minWidth: 240 }} placeholder="Search driver or asset number…" value={q} onChange={(e) => setQ(e.target.value)} />
        <button style={btnPrimary} onClick={() => setSwapOpen(true)}>⇄ Swap scanners</button>
        <label style={{ fontSize: '0.85rem', color: 'var(--hp-dim)', display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Show inactive drivers
        </label>
      </div>

      {!seeded && (
        <div style={{ ...card, marginBottom: 14, fontSize: '0.88rem' }}>
          The register is empty. Load Val&rsquo;s <code>Scanner Information.xlsx</code> on the{' '}
          <button style={{ ...btnSmall, display: 'inline' }} onClick={onImport}>Import</button> tab to start it off —
          it reconciles the sheet against the driver list and the Documents page before anything is saved.
        </div>
      )}
      {(noScanner.length > 0 || doubled.length > 0 || mismatched.length > 0) && (
        <div style={{ ...card, marginBottom: 14, fontSize: '0.85rem', display: 'grid', gap: 6 }}>
          {noScanner.length > 0 && (
            <div><Warn>{noScanner.length} active {noScanner.length === 1 ? 'driver has' : 'drivers have'} no scanner</Warn>{' '}
              <span style={{ color: 'var(--hp-dim)' }}>{noScanner.map((d) => d.name || d.login).join(', ')}</span></div>
          )}
          {doubled.length > 0 && (
            <div><Warn>{doubled.length} carrying two or more</Warn>{' '}
              <span style={{ color: 'var(--hp-dim)' }}>{doubled.map((d) => d.name || d.login).join(', ')}</span></div>
          )}
          {mismatched.length > 0 && (
            <div><Warn tone="bad">{mismatched.length} Documents record{mismatched.length === 1 ? '' : 's'} out of step</Warn>{' '}
              <span style={{ color: 'var(--hp-dim)' }}>
                {mismatched.map((d) => d.name || d.login).join(', ')} — the number on their Documents page isn&rsquo;t what this register says.
              </span>{' '}
              <button style={btnSmall} disabled={busy}
                onClick={() => act({ action: 'syncAllDms' }, 'Documents records re-synced from the register.')}>
                Fix from the register
              </button>
            </div>
          )}
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          <th style={th}>Driver</th><th style={th}>Login</th><th style={th}>Asset number</th>
          <th style={th}>Model</th><th style={th}>Started using</th><th style={th}></th>
        </tr></thead>
        <tbody>
          {rows.map((d) => {
            const held = byLogin.get(d.login) || [];
            const rec = (dms[d.login] || '').trim();
            const mismatch = isMismatch(d);
            return (
              <tr key={d.login} style={{ opacity: d.active ? 1 : 0.55 }}>
                <td style={cell}>
                  {d.name || d.login}
                  {!d.active && <span style={{ color: 'var(--hp-dim)', fontSize: '0.75rem' }}> · inactive</span>}
                  {d.area && <div style={{ color: 'var(--hp-dim)', fontSize: '0.75rem' }}>{d.area}</div>}
                </td>
                <td style={{ ...cell, ...mono, color: 'var(--hp-dim)' }}>{d.login}</td>
                <td style={cell}>
                  {held.length === 0 ? <Warn>No scanner</Warn> : held.map((s) => (
                    <div key={s.asset} style={mono}>{s.asset}{s.fleet === 'peak' && <> <PeakTag /></>}</div>
                  ))}
                  {held.length > 1 && <div><Warn>carrying {held.length}</Warn></div>}
                  {mismatch && <div style={{ marginTop: 4 }}><Warn tone="bad">Documents says {rec || '(blank)'}</Warn></div>}
                </td>
                <td style={cell}>{held.map((s) => <div key={s.asset}>{s.model}</div>)}</td>
                <td style={cell}>
                  {held.map((s) => {
                    const n = daysSince(s.since);
                    return <div key={s.asset}>{fmtDate(s.since)}<span style={{ color: 'var(--hp-dim)', fontSize: '0.75rem' }}>{n === null ? '' : ` · ${n} d`}</span></div>;
                  })}
                </td>
                <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                  <button style={btnSmall} disabled={busy} onClick={() => setAssignFor(d.login)}>
                    {held.length ? 'Change' : 'Give a scanner'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {swapOpen && <SwapModal scanners={scanners} driverName={driverName} act={act} busy={busy} onClose={() => setSwapOpen(false)} />}
      {assignFor && (
        <AssignModal login={assignFor} scanners={scanners} driverName={driverName} act={act} busy={busy}
          held={byLogin.get(assignFor) || []} onClose={() => setAssignFor(null)} />
      )}
    </div>
  );
}

// ── Swap ──────────────────────────────────────────────────────────────────────
// Both halves of a swap in one action. The spreadsheet drifted because someone typed
// one side and forgot the other; here it's atomic or it doesn't happen.
function SwapModal({ scanners, driverName, act, busy, onClose }) {
  const swappable = scanners.filter((s) => s.status === 'assigned' || s.status === 'spare');
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [since, setSince] = useState(todayISO());
  const [note, setNote] = useState('');
  const sa = swappable.find((s) => s.asset === a);
  const sb = swappable.find((s) => s.asset === b);
  const who = (s) => (s ? (s.status === 'assigned' ? driverName(s.holder) : 'the spare pool') : '');

  const label = (s) => `${s.asset} — ${s.status === 'assigned' ? driverName(s.holder) : 'SPARE'}`;

  return (
    <Modal title="Swap two scanners" onClose={onClose}>
      <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: 'var(--hp-dim)' }}>
        The two scanners change hands with each other. Either side can be a spare — that&rsquo;s how you hand out a
        replacement and take the old one back in one move.
      </p>
      <Row label="Scanner A">
        <select style={{ ...field, width: '100%' }} value={a} onChange={(e) => setA(e.target.value)}>
          <option value="">Choose…</option>
          {swappable.filter((s) => s.asset !== b).map((s) => <option key={s.asset} value={s.asset}>{label(s)}</option>)}
        </select>
      </Row>
      <Row label="Scanner B">
        <select style={{ ...field, width: '100%' }} value={b} onChange={(e) => setB(e.target.value)}>
          <option value="">Choose…</option>
          {swappable.filter((s) => s.asset !== a).map((s) => <option key={s.asset} value={s.asset}>{label(s)}</option>)}
        </select>
      </Row>
      <Row label="Date of swap"><input type="date" style={field} value={since} onChange={(e) => setSince(e.target.value)} /></Row>
      <Row label="Note"><input style={{ ...field, width: '100%' }} placeholder="optional — why" value={note} onChange={(e) => setNote(e.target.value)} /></Row>

      {sa && sb && (
        <div style={{ ...card, margin: '12px 0', fontSize: '0.88rem' }}>
          <div><strong>{who(sa)}</strong> → <span style={mono}>{sb.asset}</span></div>
          <div><strong>{who(sb)}</strong> → <span style={mono}>{sa.asset}</span></div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={btn} onClick={onClose}>Cancel</button>
        <button style={btnPrimary} disabled={busy || !sa || !sb}
          onClick={() => act({ action: 'swap', assetA: a, assetB: b, since, note }, 'Scanners swapped.').then(onClose).catch(() => {})}>
          Swap
        </button>
      </div>
    </Modal>
  );
}

// ── Assign / change a driver's scanner ────────────────────────────────────────
function AssignModal({ login, held, scanners, driverName, act, busy, onClose }) {
  const available = scanners.filter((s) => s.status === 'spare' || (s.status === 'assigned' && s.holder !== login));
  const [asset, setAsset] = useState('');
  const [since, setSince] = useState(todayISO());
  const [note, setNote] = useState('');
  const [returning, setReturning] = useState(held.length === 1 ? held[0].asset : '');
  const picked = scanners.find((s) => s.asset === asset);

  const run = async () => {
    // Taking the old one back first keeps the driver from momentarily holding two.
    if (returning) await act({ action: 'release', asset: returning, to: 'spare', since, note: 'returned on swap' }, 'Old scanner returned to the pool.');
    if (asset) await act({ action: 'assign', asset, login, since, note }, `${asset} assigned to ${driverName(login)}.`);
    onClose();
  };

  return (
    <Modal title={`Scanner for ${driverName(login)}`} onClose={onClose}>
      {held.length > 0 && (
        <Row label="Take back">
          <select style={{ ...field, width: '100%' }} value={returning} onChange={(e) => setReturning(e.target.value)}>
            <option value="">Keep what they have</option>
            {held.map((s) => <option key={s.asset} value={s.asset}>{s.asset} → back to the spare pool</option>)}
          </select>
        </Row>
      )}
      <Row label="Give them">
        <select style={{ ...field, width: '100%' }} value={asset} onChange={(e) => setAsset(e.target.value)}>
          <option value="">Nothing (just take the old one back)</option>
          {available.map((s) => (
            <option key={s.asset} value={s.asset}>
              {s.asset} — {s.status === 'spare' ? 'SPARE' : `currently ${driverName(s.holder)}`}
            </option>
          ))}
        </select>
      </Row>
      {picked && picked.status === 'assigned' && (
        <p style={{ fontSize: '0.82rem', color: '#b45309', margin: '0 0 10px' }}>
          {driverName(picked.holder)} is carrying that one — they&rsquo;ll be left without a scanner. Use <strong>Swap</strong> instead if they&rsquo;re trading.
        </p>
      )}
      <Row label="From"><input type="date" style={field} value={since} onChange={(e) => setSince(e.target.value)} /></Row>
      <Row label="Note"><input style={{ ...field, width: '100%' }} value={note} onChange={(e) => setNote(e.target.value)} /></Row>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={btn} onClick={onClose}>Cancel</button>
        <button style={btnPrimary} disabled={busy || (!asset && !returning)} onClick={() => run().catch(() => {})}>Save</button>
      </div>
    </Modal>
  );
}

// ── Register ──────────────────────────────────────────────────────────────────
function RegisterTab({ scanners, drivers, act, busy, driverName }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('active');   // active = everything but retired
  const [fleet, setFleet] = useState('all');        // all | core | peak
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [acting, setActing] = useState(null);       // {asset, mode}

  const rows = scanners
    .filter((s) => (status === 'all' ? true : status === 'active' ? s.status !== 'retired' : s.status === status))
    .filter((s) => (fleet === 'all' ? true : (s.fleet || 'core') === fleet))
    .filter((s) => {
      if (!q.trim()) return true;
      const hay = `${s.asset} ${s.model} ${s.holder} ${driverName(s.holder)} ${s.note}`.toLowerCase();
      return hay.includes(q.trim().toLowerCase());
    });

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <input style={{ ...field, minWidth: 240 }} placeholder="Search asset number, driver, note…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select style={field} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="active">In service (all but retired)</option>
          <option value="assigned">With a driver</option>
          <option value="spare">Spare</option>
          <option value="repair">At repair</option>
          <option value="retired">Retired</option>
          <option value="all">Everything</option>
        </select>
        <select style={field} value={fleet} onChange={(e) => setFleet(e.target.value)}>
          <option value="all">Both fleets</option>
          <option value="core">Our own</option>
          <option value="peak">Peak loans only</option>
        </select>
        <button style={btnPrimary} onClick={() => setAdding(true)}>+ Add scanner</button>
        <span style={{ color: 'var(--hp-dim)', fontSize: '0.82rem' }}>{rows.length} shown</span>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          <th style={th}>Asset number</th><th style={th}>Model</th><th style={th}>Status</th>
          <th style={th}>Holder</th><th style={th}>Since</th><th style={th}>Note</th><th style={th}></th>
        </tr></thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.asset}>
              <td style={{ ...cell, ...mono }}>{s.asset}{s.fleet === 'peak' && <> <PeakTag /></>}</td>
              <td style={cell}>{s.model}</td>
              <td style={cell}>
                <Pill status={s.status} />
                {s.status === 'repair' && s.repair && s.repair.expectedBack && s.repair.expectedBack < todayISO()
                  && <div style={{ marginTop: 4 }}><Warn tone="bad">overdue</Warn></div>}
              </td>
              <td style={cell}>{s.status === 'assigned' ? <>{driverName(s.holder)}<div style={{ ...mono, color: 'var(--hp-dim)', fontSize: '0.75rem' }}>{s.holder}</div></> : '—'}</td>
              <td style={cell}>{fmtDate(s.since)}</td>
              <td style={{ ...cell, whiteSpace: 'normal', maxWidth: 260, color: 'var(--hp-dim)', fontSize: '0.8rem' }}>
                {s.note}
                {s.status === 'repair' && s.repair && (
                  <div>{s.repair.reason}{s.repair.ref ? ` · ref ${s.repair.ref}` : ''}{s.repair.expectedBack ? ` · back ${fmtDate(s.repair.expectedBack)}` : ''}</div>
                )}
                {s.fleet === 'peak' && s.status !== 'retired' && (
                  <div>Due back to AP {s.dueBack ? fmtDate(s.dueBack) : '— no date set'}
                    {s.dueBack && s.dueBack < todayISO() && <> <Warn tone="bad">overdue</Warn></>}</div>
                )}
              </td>
              <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                <button style={btnSmall} onClick={() => setEditing(s)}>Edit</button>{' '}
                <button style={btnSmall} onClick={() => setActing({ scanner: s })}>Move</button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={{ ...cell, color: 'var(--hp-dim)' }} colSpan={7}>Nothing matches.</td></tr>}
        </tbody>
      </table>

      {adding && <AddModal drivers={drivers} act={act} busy={busy} onClose={() => setAdding(false)} />}
      {editing && <EditModal scanner={editing} act={act} busy={busy} onClose={() => setEditing(null)} />}
      {acting && <MoveModal scanner={acting.scanner} drivers={drivers} driverName={driverName} act={act} busy={busy} onClose={() => setActing(null)} />}
    </div>
  );
}

function AddModal({ drivers, act, busy, onClose }) {
  const [f, setF] = useState({ asset: '', model: 'TC58e', fleet: 'core', dueBack: '', status: 'spare', holder: '', since: todayISO(), note: '', reason: '', ref: '', expectedBack: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title="Add a scanner" onClose={onClose}>
      <Row label="Asset number"><input style={{ ...field, ...mono, width: '100%' }} value={f.asset} onChange={set('asset')} placeholder="e.g. 25083524202519" /></Row>
      <Row label="Model"><input style={field} value={f.model} onChange={set('model')} /></Row>
      <Row label="Fleet">
        <select style={field} value={f.fleet} onChange={set('fleet')}>
          <option value="core">Ours — all year</option>
          <option value="peak">Peak loan from AP</option>
        </select>
      </Row>
      {f.fleet === 'peak' && (
        <Row label="Due back to AP"><input type="date" style={field} value={f.dueBack} onChange={set('dueBack')} /></Row>
      )}
      <Row label="Status">
        <select style={field} value={f.status} onChange={set('status')}>
          <option value="spare">Spare</option><option value="assigned">With a driver</option>
          <option value="repair">At repair</option><option value="retired">Retired</option>
        </select>
      </Row>
      {f.status === 'assigned' && (
        <Row label="Driver">
          <select style={{ ...field, width: '100%' }} value={f.holder} onChange={set('holder')}>
            <option value="">Choose…</option>
            {drivers.filter((d) => d.active).map((d) => <option key={d.login} value={d.login}>{d.name || d.login} ({d.login})</option>)}
          </select>
        </Row>
      )}
      {f.status === 'repair' && (
        <>
          <Row label="Reason"><input style={{ ...field, width: '100%' }} value={f.reason} onChange={set('reason')} /></Row>
          <Row label="Repair ref"><input style={field} value={f.ref} onChange={set('ref')} /></Row>
          <Row label="Expected back"><input type="date" style={field} value={f.expectedBack} onChange={set('expectedBack')} /></Row>
        </>
      )}
      <Row label="Date"><input type="date" style={field} value={f.since} onChange={set('since')} /></Row>
      <Row label="Note"><input style={{ ...field, width: '100%' }} value={f.note} onChange={set('note')} /></Row>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={btn} onClick={onClose}>Cancel</button>
        <button style={btnPrimary} disabled={busy || !f.asset.trim()}
          onClick={() => act({ action: 'addScanner', ...f }, `${f.asset} added.`).then(onClose).catch(() => {})}>Add</button>
      </div>
    </Modal>
  );
}

function EditModal({ scanner, act, busy, onClose }) {
  const [model, setModel] = useState(scanner.model);
  const [since, setSince] = useState(scanner.since || todayISO());
  const [note, setNote] = useState(scanner.note || '');
  const [fleet, setFleet] = useState(scanner.fleet || 'core');
  const [dueBack, setDueBack] = useState(scanner.dueBack || '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  return (
    <Modal title={scanner.asset} onClose={onClose}>
      <Row label="Model"><input style={field} value={model} onChange={(e) => setModel(e.target.value)} /></Row>
      <Row label="Started using"><input type="date" style={field} value={since} onChange={(e) => setSince(e.target.value)} /></Row>
      <Row label="Fleet">
        <select style={field} value={fleet} onChange={(e) => setFleet(e.target.value)}>
          <option value="core">Ours — all year</option>
          <option value="peak">Peak loan from AP</option>
        </select>
      </Row>
      {fleet === 'peak' && (
        <Row label="Due back to AP"><input type="date" style={field} value={dueBack} onChange={(e) => setDueBack(e.target.value)} /></Row>
      )}
      <Row label="Note"><input style={{ ...field, width: '100%' }} value={note} onChange={(e) => setNote(e.target.value)} /></Row>
      <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
        To change who has it, close this and use <strong>Move</strong> or <strong>Swap</strong> — those write the custody log.
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
        <div>
          {confirmDelete ? (
            <button style={{ ...btn, borderColor: '#dc2626', color: '#dc2626' }} disabled={busy}
              onClick={() => act({ action: 'removeScanner', asset: scanner.asset }, `${scanner.asset} removed from the register.`).then(onClose).catch(() => {})}>
              Really delete {scanner.asset}
            </button>
          ) : (
            <button style={btn} onClick={() => setConfirmDelete(true)} title="Only for a number typed wrong — use Retire for a scanner that has left the fleet">
              Delete entry
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btn} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} disabled={busy}
            onClick={() => act({ action: 'updateScanner', asset: scanner.asset, model, since, note, fleet, dueBack }, 'Saved.').then(onClose).catch(() => {})}>Save</button>
        </div>
      </div>
    </Modal>
  );
}

// One modal for every custody move a single scanner can make.
function MoveModal({ scanner, drivers, driverName, act, busy, onClose }) {
  const atRepair = scanner.status === 'repair';
  const [to, setTo] = useState(atRepair ? 'back' : scanner.status === 'assigned' ? 'spare' : 'assigned');
  const [login, setLogin] = useState('');
  const [since, setSince] = useState(todayISO());
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [ref, setRef] = useState('');
  const [expectedBack, setExpectedBack] = useState('');

  const run = () => {
    if (atRepair) {
      return act({ action: 'returnFromRepair', asset: scanner.asset, login: to === 'back-driver' ? login : '', since, note },
        `${scanner.asset} back from repair.`);
    }
    if (to === 'assigned') {
      return act({ action: 'assign', asset: scanner.asset, login, since, note }, `${scanner.asset} assigned to ${driverName(login)}.`);
    }
    return act({ action: 'release', asset: scanner.asset, to, since, note, reason, ref, expectedBack },
      to === 'repair' ? `${scanner.asset} marked as away for repair.` : to === 'retired' ? `${scanner.asset} retired.` : `${scanner.asset} returned to the spare pool.`);
  };

  return (
    <Modal title={`Move ${scanner.asset}`} onClose={onClose}>
      <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: 'var(--hp-dim)' }}>
        Currently <Pill status={scanner.status} />{scanner.status === 'assigned' ? ` with ${driverName(scanner.holder)}` : ''} since {fmtDate(scanner.since)}.
      </p>
      <Row label="Move to">
        <select style={{ ...field, width: '100%' }} value={to} onChange={(e) => setTo(e.target.value)}>
          {atRepair ? (
            <>
              <option value="back">Back — into the spare pool</option>
              <option value="back-driver">Back — straight to a driver</option>
            </>
          ) : (
            <>
              <option value="assigned">A driver</option>
              <option value="spare">The spare pool</option>
              <option value="repair">Away for repair</option>
              <option value="retired">Retired / handed back to AP</option>
            </>
          )}
        </select>
      </Row>
      {(to === 'assigned' || to === 'back-driver') && (
        <Row label="Driver">
          <select style={{ ...field, width: '100%' }} value={login} onChange={(e) => setLogin(e.target.value)}>
            <option value="">Choose…</option>
            {drivers.filter((d) => d.active).map((d) => <option key={d.login} value={d.login}>{d.name || d.login} ({d.login})</option>)}
          </select>
        </Row>
      )}
      {to === 'repair' && (
        <>
          <Row label="What's wrong"><input style={{ ...field, width: '100%' }} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. camera won't focus" /></Row>
          <Row label="Repair ref"><input style={field} value={ref} onChange={(e) => setRef(e.target.value)} placeholder="AP / Zebra reference" /></Row>
          <Row label="Expected back"><input type="date" style={field} value={expectedBack} onChange={(e) => setExpectedBack(e.target.value)} /></Row>
        </>
      )}
      <Row label="Date"><input type="date" style={field} value={since} onChange={(e) => setSince(e.target.value)} /></Row>
      <Row label="Note"><input style={{ ...field, width: '100%' }} value={note} onChange={(e) => setNote(e.target.value)} /></Row>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button style={btn} onClick={onClose}>Cancel</button>
        <button style={btnPrimary} disabled={busy || ((to === 'assigned' || to === 'back-driver') && !login)}
          onClick={() => run().then(onClose).catch(() => {})}>Save</button>
      </div>
    </Modal>
  );
}

// ── Spares & Repairs ──────────────────────────────────────────────────────────
function PoolTab({ scanners, drivers, act, busy }) {
  const spares = scanners.filter((s) => s.status === 'spare');
  const repairs = scanners.filter((s) => s.status === 'repair');
  // Every Christmas loan still in our hands, whoever holds it — the one list to
  // read down when AP asks for them back in January.
  const peak = scanners.filter((s) => s.fleet === 'peak' && s.status !== 'retired');
  const [acting, setActing] = useState(null);
  const driverName = (l) => { const d = drivers.find((x) => x.login === l); return d ? (d.name || d.login) : l; };

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <section>
        <h2 style={{ fontSize: '1rem', margin: '0 0 8px' }}>Spare pool <span style={{ color: 'var(--hp-dim)', fontWeight: 400 }}>({spares.length})</span></h2>
        {spares.length === 0 ? <p style={{ color: 'var(--hp-dim)', fontSize: '0.88rem' }}>No spares on hand.</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Asset number</th><th style={th}>Model</th><th style={th}>Spare since</th><th style={th}>Note</th><th style={th}></th></tr></thead>
            <tbody>
              {spares.map((s) => (
                <tr key={s.asset}>
                  <td style={{ ...cell, ...mono }}>{s.asset}</td>
                  <td style={cell}>{s.model}</td>
                  <td style={cell}>{fmtDate(s.since)}</td>
                  <td style={{ ...cell, color: 'var(--hp-dim)', fontSize: '0.8rem', whiteSpace: 'normal' }}>{s.note}</td>
                  <td style={cell}><button style={btnSmall} onClick={() => setActing(s)}>Hand out</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h2 style={{ fontSize: '1rem', margin: '0 0 8px' }}>Away for repair <span style={{ color: 'var(--hp-dim)', fontWeight: 400 }}>({repairs.length})</span></h2>
        {repairs.length === 0 ? <p style={{ color: 'var(--hp-dim)', fontSize: '0.88rem' }}>Nothing away being fixed.</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Asset number</th><th style={th}>Sent</th><th style={th}>Fault</th><th style={th}>Ref</th><th style={th}>Expected back</th><th style={th}></th></tr></thead>
            <tbody>
              {repairs.map((s) => {
                const r = s.repair || {};
                const overdue = r.expectedBack && r.expectedBack < todayISO();
                return (
                  <tr key={s.asset}>
                    <td style={{ ...cell, ...mono }}>{s.asset}</td>
                    <td style={cell}>{fmtDate(r.sentAt || s.since)}<div style={{ color: 'var(--hp-dim)', fontSize: '0.75rem' }}>{daysSince(r.sentAt || s.since)} d ago</div></td>
                    <td style={{ ...cell, whiteSpace: 'normal', maxWidth: 280 }}>{r.reason || s.note || '—'}</td>
                    <td style={{ ...cell, ...mono }}>{r.ref || '—'}</td>
                    <td style={cell}>{r.expectedBack ? fmtDate(r.expectedBack) : '—'} {overdue && <Warn tone="bad">overdue</Warn>}</td>
                    <td style={cell}><button style={btnSmall} onClick={() => setActing(s)}>Back from repair</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {peak.length > 0 && (
        <section>
          <h2 style={{ fontSize: '1rem', margin: '0 0 8px' }}>
            Peak loans from AP <span style={{ color: 'var(--hp-dim)', fontWeight: 400 }}>({peak.length})</span>
          </h2>
          <p style={{ margin: '0 0 8px', fontSize: '0.85rem', color: 'var(--hp-dim)' }}>
            Christmas scanners AP lends us for the season. They are not ours — each one goes back, so it is listed here
            whether it is with a driver, on the shelf or at repair. Send one back with <strong>Move → Retired</strong>,
            which records the date and keeps the custody history.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Asset number</th><th style={th}>Model</th><th style={th}>Where it is</th><th style={th}>Since</th><th style={th}>Due back</th><th style={th}></th></tr></thead>
            <tbody>
              {peak.map((s) => {
                const overdue = s.dueBack && s.dueBack < todayISO();
                return (
                  <tr key={s.asset}>
                    <td style={{ ...cell, ...mono }}>{s.asset}</td>
                    <td style={cell}>{s.model}</td>
                    <td style={cell}>
                      <Pill status={s.status} />
                      {s.status === 'assigned' && <div style={{ fontSize: '0.8rem' }}>{driverName(s.holder)}<div style={{ ...mono, color: 'var(--hp-dim)', fontSize: '0.75rem' }}>{s.holder}</div></div>}
                    </td>
                    <td style={cell}>{fmtDate(s.since)}</td>
                    <td style={cell}>{s.dueBack ? fmtDate(s.dueBack) : <span style={{ color: 'var(--hp-dim)' }}>no date set</span>} {overdue && <Warn tone="bad">overdue</Warn>}</td>
                    <td style={cell}><button style={btnSmall} onClick={() => setActing(s)}>Move</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {acting && <MoveModal scanner={acting} drivers={drivers} driverName={driverName} act={act} busy={busy} onClose={() => setActing(null)} />}
    </div>
  );
}

// ── History ───────────────────────────────────────────────────────────────────
const ACTION_LABEL = {
  assign: 'Given to driver', swap: 'Swapped', spare: 'Back to spare pool', repair: 'Sent for repair',
  returned: 'Back from repair', retire: 'Retired', add: 'Added to register', edit: 'Details edited',
  remove: 'Removed from register', import: 'Imported',
};
function HistoryTab({ moves, driverName }) {
  const [q, setQ] = useState('');
  const rows = moves.filter((m) => {
    if (!q.trim()) return true;
    const hay = `${m.asset} ${m.fromLogin} ${m.toLogin} ${driverName(m.fromLogin)} ${driverName(m.toLogin)} ${ACTION_LABEL[m.action] || m.action} ${m.note}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });
  const side = (status, login) => (status === 'assigned' ? driverName(login) : status ? (STATUS_STYLE[status] || {}).label || status : '—');

  return (
    <div>
      <input style={{ ...field, minWidth: 280, marginBottom: 14 }} placeholder="Search asset number, driver, action…" value={q} onChange={(e) => setQ(e.target.value)} />
      <p style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', margin: '0 0 10px' }}>
        Every custody change, newest first. {rows.length} of {moves.length} shown.
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={th}>When</th><th style={th}>Asset</th><th style={th}>What</th><th style={th}>From</th><th style={th}>To</th><th style={th}>Note</th><th style={th}>By</th></tr></thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.id}>
              <td style={{ ...cell, whiteSpace: 'nowrap', color: 'var(--hp-dim)' }}>{fmtWhen(m.at)}</td>
              <td style={{ ...cell, ...mono }}>{m.asset}</td>
              <td style={cell}>{ACTION_LABEL[m.action] || m.action}</td>
              <td style={cell}>{side(m.fromStatus, m.fromLogin)}</td>
              <td style={cell}>{side(m.toStatus, m.toLogin)}</td>
              <td style={{ ...cell, color: 'var(--hp-dim)', fontSize: '0.8rem', whiteSpace: 'normal', maxWidth: 260 }}>{m.note}</td>
              <td style={{ ...cell, color: 'var(--hp-dim)', fontSize: '0.78rem' }}>{m.by}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={{ ...cell, color: 'var(--hp-dim)' }} colSpan={7}>Nothing yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ── Asset audit ───────────────────────────────────────────────────────────────
// Ask drivers to photograph their scanner's home screen; the reply comes back with
// the asset number read off the device itself, which is the only evidence that
// doesn't depend on somebody remembering to tell the office about a swap.
const GUIDE_URL = '/scanner-home-screen.html';

function AuditTab({ data, act, busy }) {
  const [requests, setRequests] = useState(null);
  const [reqErr, setReqErr] = useState('');
  const [picked, setPicked] = useState(() => new Set());
  const [note, setNote] = useState('');
  const [q, setQ] = useState('');

  // Ordered and labelled by LOGIN, not by name — you pick who to chase off the
  // login list you already read everywhere else (the register, the audits, AP's
  // own reports), so sorting by contact name would put the list in an order that
  // matches nothing else on the page.
  // Night sorters are left out: they carry no scanner, and a check sent to one
  // would put a driver's brief in front of somebody with nothing to photograph.
  const hiddenSorters = data.drivers.filter((d) => d.active && d.sorterOnly);
  const drivers = data.drivers
    .filter((d) => d.active && !d.sorterOnly)
    .slice()
    .sort((a, b) => a.login.localeCompare(b.login));
  const byLogin = useMemo(() => {
    const m = new Map();
    data.scanners.filter((s) => s.status === 'assigned' && s.holder).forEach((s) => {
      if (!m.has(s.holder)) m.set(s.holder, []);
      m.get(s.holder).push(s);
    });
    return m;
  }, [data.scanners]);

  const loadRequests = useCallback(() => {
    post({ action: 'auditList' })
      .then((j) => { setRequests(j.requests); setReqErr(''); })
      .catch((e) => { setRequests([]); setReqErr(e.message); });
  }, []);
  useEffect(() => { loadRequests(); }, [loadRequests]);

  const run = (body, describe) => act(body, describe).then(loadRequests).catch(() => {});

  const open = (requests || []).filter((r) => r.status !== 'done');
  const done = (requests || []).filter((r) => r.status === 'done');
  const waiting = open.filter((r) => r.status === 'submitted');
  const asked = new Set((requests || []).filter((r) => r.status !== 'done').map((r) => r.login));

  const shown = drivers.filter((d) => !q.trim() || `${d.name} ${d.login}`.toLowerCase().includes(q.trim().toLowerCase()));
  const allShownPicked = shown.length > 0 && shown.every((d) => picked.has(d.login));
  const toggle = (login) => setPicked((p) => { const n = new Set(p); if (n.has(login)) n.delete(login); else n.add(login); return n; });

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div style={{ ...card }}>
        <h2 style={{ fontSize: '1rem', margin: '0 0 6px' }}>Ask for a scanner check</h2>
        <p style={{ margin: '0 0 10px', fontSize: '0.85rem', color: 'var(--hp-dim)' }}>
          The driver gets a card on the portal&rsquo;s Scanner tab asking for a photo of the handheld&rsquo;s home screen — the
          one showing the date, the asset number and the model. We read the number off the photo, they confirm it against
          the screen in their hand, and you accept it into the register. They can open{' '}
          <a href={GUIDE_URL} target="_blank" rel="noreferrer" style={{ color: 'var(--hp-accent, #2563eb)' }}>the step-by-step guide</a>{' '}
          from the same card.
        </p>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <input style={{ ...field, minWidth: 200 }} placeholder="Filter drivers…" value={q} onChange={(e) => setQ(e.target.value)} />
          <button style={btnSmall} onClick={() => setPicked(allShownPicked ? new Set() : new Set(shown.map((d) => d.login)))}>
            {allShownPicked ? 'Deselect all' : `Select all ${shown.length}`}
          </button>
          <button style={btnSmall} onClick={() => setPicked(new Set(shown.filter((d) => !(byLogin.get(d.login) || []).length).map((d) => d.login)))}>
            Only those with no scanner on file
          </button>
          <button style={btnSmall} onClick={() => setPicked(new Set())}>Clear</button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 210, overflowY: 'auto', marginBottom: 10 }}>
          {shown.map((d) => {
            const on = picked.has(d.login);
            const held = byLogin.get(d.login) || [];
            return (
              <button key={d.login} onClick={() => toggle(d.login)}
                title={`${d.name || d.login} — ${held.length ? held.map((s) => s.asset).join(', ') : 'no scanner on file'}`}
                style={{
                  ...btnSmall,
                  background: on ? 'var(--hp-accent, #2563eb)' : 'var(--hp-surface)',
                  color: on ? '#fff' : 'var(--hp-text)',
                  borderColor: on ? 'transparent' : 'var(--hp-border)',
                }}>
                <span style={mono}>{d.login}</span>{asked.has(d.login) ? ' · already asked' : ''}{held.length ? '' : ' · no scanner'}
              </button>
            );
          })}
        </div>

        {hiddenSorters.length > 0 && (
          <p style={{ margin: '0 0 10px', fontSize: '0.8rem', color: 'var(--hp-dim)' }}
             title={hiddenSorters.map((d) => `${d.login}${d.name ? ` — ${d.name}` : ''}`).join('\n')}>
            {hiddenSorters.length} night-sort {hiddenSorters.length === 1 ? 'login is' : 'logins are'} not listed
            ({hiddenSorters.map((d) => d.login).join(', ')}) — they don&rsquo;t carry a scanner.
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input style={{ ...field, flex: 1, minWidth: 240 }} placeholder="Note the driver will see (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <button style={btnPrimary} disabled={busy || picked.size === 0}
            onClick={() => run({ action: 'auditRequest', logins: [...picked], note },
              `Asked ${picked.size} driver${picked.size === 1 ? '' : 's'} for a scanner check.`).then(() => { setPicked(new Set()); setNote(''); })}>
            {picked.size === 0 ? 'Ask' : allShownPicked && !q.trim() ? `Ask all ${picked.size} drivers` : `Ask ${picked.size}`}
          </button>
        </div>
      </div>

      {reqErr && <div style={{ ...card, borderColor: '#dc2626', color: '#dc2626' }}>{reqErr}</div>}
      {requests === null && <p style={{ color: 'var(--hp-dim)' }}>Loading replies…</p>}

      {open.length > 0 && (
        <section>
          <h2 style={{ fontSize: '1rem', margin: '0 0 8px' }}>
            Open ({open.length}) <span style={{ color: 'var(--hp-dim)', fontWeight: 400 }}>· {waiting.length} waiting on you</span>
          </h2>
          <div style={{ display: 'grid', gap: 12 }}>
            {open.map((r) => <AuditCard key={r.login} r={r} held={byLogin.get(r.login) || []} run={run} busy={busy} />)}
          </div>
        </section>
      )}

      {done.length > 0 && (
        <section>
          <h2 style={{ fontSize: '1rem', margin: '0 0 8px' }}>Checked ({done.length})</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Driver</th><th style={th}>Login</th><th style={th}>Asset</th><th style={th}>Device said</th><th style={th}>Checked</th><th style={th}></th></tr></thead>
            <tbody>
              {done.map((r) => (
                <tr key={r.login}>
                  <td style={cell}>{r.name || r.login}</td>
                  <td style={{ ...cell, ...mono, color: 'var(--hp-dim)' }}>{r.login}</td>
                  <td style={{ ...cell, ...mono }}>{(r.accepted && r.accepted.asset) || '—'}</td>
                  <td style={{ ...cell, color: 'var(--hp-dim)', fontSize: '0.8rem' }}>
                    {r.ocr ? `${r.ocr.deviceDate || '—'}${r.ocr.battery == null ? '' : ` · ${r.ocr.battery}%`}` : '—'}
                  </td>
                  <td style={{ ...cell, color: 'var(--hp-dim)', fontSize: '0.8rem' }}>{fmtWhen(r.reviewedAt)} · {r.reviewedBy}</td>
                  <td style={cell}>
                    {r.viewUrl && <a href={r.viewUrl} target="_blank" rel="noreferrer" style={{ ...btnSmall, textDecoration: 'none' }}>Photo</a>}{' '}
                    <button style={btnSmall} disabled={busy} onClick={() => run({ action: 'auditCancel', login: r.login }, 'Cleared.')}>Clear</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {requests !== null && requests.length === 0 && (
        <p style={{ color: 'var(--hp-dim)', fontSize: '0.88rem' }}>No checks outstanding. Pick some drivers above to start one.</p>
      )}
    </div>
  );
}

function AuditCard({ r, held, run, busy }) {
  const readAsset = (r.confirmed && r.confirmed.asset) || (r.ocr && r.ocr.assetNumber) || '';
  const [asset, setAsset] = useState(readAsset);
  const [model, setModel] = useState((r.confirmed && r.confirmed.model) || (r.ocr && r.ocr.model) || 'TC58e');
  // Only offered for a number we have never seen — an asset already in the register
  // keeps whichever fleet it was filed under; changing that is the Edit modal's job.
  const [peak, setPeak] = useState(false);
  useEffect(() => { setAsset(readAsset); }, [readAsset]);

  const onFile = held.map((s) => s.asset);
  const agrees = asset && onFile.includes(asset);
  const disagrees = asset && onFile.length > 0 && !agrees;
  const notHome = r.ocr && r.ocr.isHomeScreen === false;

  return (
    <div style={{ ...card, display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong>{r.name || r.login}</strong>
        <span style={{ ...mono, color: 'var(--hp-dim)' }}>{r.login}</span>
        {r.status === 'pending'
          ? <Warn>waiting on the driver — asked {fmtWhen(r.requestedAt)}</Warn>
          : <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600, background: 'rgba(34,197,94,0.14)', color: '#16a34a' }}>replied {fmtWhen(r.submittedAt)}</span>}
        <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
          register says {onFile.length ? onFile.join(', ') : 'nothing'}
        </span>
      </div>
      {r.note && <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>Note sent: {r.note}</div>}

      {r.status === 'pending' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btnSmall} disabled={busy} onClick={() => run({ action: 'auditCancel', login: r.login }, 'Request withdrawn.')}>Withdraw</button>
        </div>
      )}

      {r.status === 'submitted' && (
        <>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {r.viewUrl && (
              <a href={r.viewUrl} target="_blank" rel="noreferrer" title="Open the full photo">
                <img src={r.viewUrl} alt={`${r.login}'s scanner home screen`}
                  style={{ width: 150, borderRadius: 8, border: '1px solid var(--hp-border)', display: 'block' }} />
              </a>
            )}
            <div style={{ flex: 1, minWidth: 240, fontSize: '0.85rem', display: 'grid', gap: 4 }}>
              {r.ocrError ? (
                <div style={{ color: '#b45309' }}>
                  Couldn&rsquo;t read the photo ({r.ocrError}). Read it yourself and type the number in.
                </div>
              ) : r.ocr ? (
                <>
                  <div>Device date: <strong>{r.ocr.deviceDate || '—'}</strong>{r.ocr.battery == null ? '' : ` · battery ${r.ocr.battery}%`}</div>
                  <div>Read off the screen: <span style={mono}>{r.ocr.assetNumber || '—'}</span> · {r.ocr.model || '—'}</div>
                  {r.confirmed && <div>Driver confirmed: <span style={mono}>{r.confirmed.asset}</span> · {r.confirmed.model}</div>}
                  {!r.confirmed && <Warn>the driver hasn&rsquo;t confirmed the number yet</Warn>}
                  {r.ocr.note && <div style={{ color: '#b45309' }}>{r.ocr.note}</div>}
                  {notHome && <div><Warn tone="bad">this doesn&rsquo;t look like the home screen</Warn></div>}
                </>
              ) : null}
              {disagrees && <div><Warn tone="bad">different from the register — accepting moves it</Warn></div>}
              {agrees && <div style={{ color: '#16a34a' }}>Matches the register.</div>}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input style={{ ...field, ...mono, width: 180 }} value={asset} onChange={(e) => setAsset(e.target.value)} placeholder="asset number" />
            <input style={{ ...field, width: 90 }} value={model} onChange={(e) => setModel(e.target.value)} placeholder="model" />
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.82rem', color: 'var(--hp-dim)' }}
              title="Tick if this is one of AP's Christmas loan scanners">
              <input type="checkbox" checked={peak} onChange={(e) => setPeak(e.target.checked)} /> peak loan
            </label>
            <button style={btnPrimary} disabled={busy || !asset.trim()}
              onClick={() => run({ action: 'auditAccept', login: r.login, asset, model, fleet: peak ? 'peak' : 'core' }, `${r.name || r.login}: ${asset} recorded.`)}>
              Accept
            </button>
            <button style={btnSmall} disabled={busy} onClick={() => run({ action: 'auditReocr', login: r.login }, 'Photo re-read.')}>Re-read</button>
            <button style={btnSmall} disabled={busy}
              onClick={() => run({ action: 'auditReject', login: r.login, reason: 'That photo wasn\'t clear enough — please send another of the home screen.' }, 'Sent back to the driver.')}>
              Send back
            </button>
            <button style={btnSmall} disabled={busy} onClick={() => run({ action: 'auditCancel', login: r.login }, 'Request withdrawn.')}>Withdraw</button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Import ────────────────────────────────────────────────────────────────────
// The spreadsheet is not clean: logins drift from the app's (PENOYARL vs PENAOYARL),
// the same asset number appears against two people, and the Documents page disagrees
// with the sheet for a handful of drivers. So this is a reconciliation, not a load —
// nothing is written until every row resolves to a real driver and one holder.
const excelDate = (v) => {
  if (typeof v === 'number' && v > 20000 && v < 90000) {
    const d = new Date(Date.UTC(1899, 11, 30) + v * 86400000);
    return d.toISOString().slice(0, 10);
  }
  const s = (v || '').toString().trim();
  let m = s.match(/^(\d{1,2})[/\-.]+(\d{1,2})[/\-.]+(\d{2,4})$/);   // AU d/m/y, tolerating a doubled separator
  if (m) {
    const yr = m[3].length === 2 ? `20${m[3]}` : m[3];
    return `${yr}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  }
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? m[0] : '';
};
const cleanAsset = (v) => {
  const a = (v === null || v === undefined ? '' : v).toString().trim().toUpperCase().replace(/\s+/g, '');
  return /^[A-Z0-9][A-Z0-9-]{3,23}$/.test(a) ? a : '';
};
function lev(a, b) {
  const m = a.length, n = b.length;
  if (!m || !n) return Math.max(m, n);
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
}
// Sheet login → app login. ACTIVE drivers are tried first at every step, because a
// renamed login leaves the old one behind as an inactive row: the sheet still says
// CAIC and the app has both CAIC (inactive) and CAIC3 (the same person, active).
// An exact match on a dead login is the wrong answer, so it ranks below a name match
// on a live one — and when only the dead login fits, the row is flagged, not silently
// filed against someone who left. (PENOYARL→PENAOYARL, ANDUNCESR→ANDUNCESR2, CAIC→CAIC3.)
function matchByName(sheetName, pool) {
  const nm = (sheetName || '').trim().toLowerCase();
  if (!nm) return null;
  const words = nm.replace(/\([^)]*\)/g, ' ').split(/\s+/).filter((w) => w.length > 2);
  if (!words.length) return null;
  const hit = pool.filter((d) => {
    const dn = (d.name || '').toLowerCase();
    if (!dn) return false;
    const matched = words.filter((w) => dn.includes(w)).length;
    return matched >= 2 || (words.length === 1 && matched === 1);
  });
  return hit.length === 1 ? hit[0] : null;
}
function matchNearLogin(L, pool) {
  if (L.length < 4) return null;
  const near = pool.map((d) => ({ d, dist: lev(L, d.login) })).filter((x) => x.dist <= 2).sort((a, b) => a.dist - b.dist);
  return near.length && (near.length === 1 || near[0].dist < near[1].dist) ? near[0].d : null;
}
function resolveLogin(sheetLogin, sheetName, drivers) {
  const L = (sheetLogin || '').trim().toUpperCase();
  const live = drivers.filter((d) => d.active);

  if (L && live.some((d) => d.login === L)) return { login: L, how: 'exact' };
  const byName = matchByName(sheetName, live);
  if (byName) return { login: byName.login, how: byName.login === L ? 'exact' : 'name' };
  const byNear = matchNearLogin(L, live);
  if (byNear) return { login: byNear.login, how: 'near' };
  // Nothing live fits — fall back to the full list so the row still resolves, but say so.
  if (L && drivers.some((d) => d.login === L)) return { login: L, how: 'inactive' };
  const anyName = matchByName(sheetName, drivers);
  if (anyName) return { login: anyName.login, how: 'inactive' };
  return { login: '', how: 'none' };
}

function ImportTab({ data, act, busy, onDone }) {
  const [rows, setRows] = useState(null);          // parsed + resolved
  const [orphans, setOrphans] = useState([]);      // free-text lines we couldn't parse
  const [noScanner, setNoScanner] = useState([]);  // rows naming a driver with no scanner
  const [sheetName, setSheetName] = useState('');
  const [mode, setMode] = useState(data.seeded ? 'merge' : 'replace');
  const [parseErr, setParseErr] = useState('');
  const fileRef = useRef(null);

  const drivers = data.drivers;
  const dms = data.dms;
  const existing = useMemo(() => new Map(data.scanners.map((s) => [s.asset, s])), [data.scanners]);

  const parse = async (file) => {
    setParseErr(''); setRows(null); setOrphans([]); setNoScanner([]);
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      // Prefer the current-fleet sheet; otherwise the first sheet with a
      // "Scanner Number" header. Older sheets in the same workbook are history.
      const name = wb.SheetNames.find((n) => /tc58/i.test(n))
        || wb.SheetNames.find((n) => {
          const aoa = XLSX.utils.sheet_to_json(wb.Sheets[n], { header: 1, defval: '' }).slice(0, 6);
          return aoa.some((r) => r.some((c) => /scanner number/i.test((c || '').toString())));
        })
        || wb.SheetNames[0];
      setSheetName(name);
      const aoa = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: '' });
      const hIdx = aoa.findIndex((r) => r.some((c) => /scanner number/i.test((c || '').toString())));
      if (hIdx < 0) throw new Error('No "Scanner Number" column found on that sheet.');
      const head = aoa[hIdx].map((c) => (c || '').toString().trim().toLowerCase());
      const col = (re, fallback) => { const i = head.findIndex((h) => re.test(h)); return i >= 0 ? i : fallback; };
      const cName = col(/^name$/, 0);
      const cLogin = col(/logon|login/, 2);
      const cAsset = col(/scanner number/, 3);
      const cModel = col(/model/, 5);
      const cSince = col(/date started/, 7);
      const cNote = head.length - 1;

      const out = [];
      const stray = [];      // free prose at the foot of the sheet
      const blank = [];      // real people the sheet lists with no scanner
      for (let i = hIdx + 1; i < aoa.length; i++) {
        const r = aoa[i];
        const name0 = (r[cName] || '').toString().trim();
        const login0 = (r[cLogin] || '').toString().trim();
        const asset = cleanAsset(r[cAsset]);
        if (!name0 && !login0 && !asset) continue;
        // The bottom of Val's sheet is free prose ("Rey has Sam's old scanner - …").
        // Those are real instructions, but not rows — surface them, don't guess. A row
        // that names a driver but has "-" for a scanner is different: that's a real
        // person the sheet says has nothing, which is worth reading as its own list.
        if (!asset) {
          if (login0 && login0 !== '-' && name0.length <= 45) blank.push(`${name0}${login0 ? ` (${login0})` : ''}`);
          else stray.push(name0 || login0 || (r[cAsset] || '').toString());
          continue;
        }
        if (!login0 && name0.length > 45) { stray.push(name0); continue; }

        const looksSpare = /spare/i.test(name0) || /^(spare|-)$/i.test(login0) || !login0;
        const looksBroken = /broken|no laser|not work|faulty/i.test(`${name0} ${r[cNote] || ''}`);
        const res = looksSpare ? { login: '', how: 'spare' } : resolveLogin(login0, name0, drivers);
        out.push({
          key: `r${i}`,
          sheetName: name0,
          sheetLogin: login0,
          asset,
          model: (r[cModel] || '').toString().trim() || 'TC58e',
          since: excelDate(r[cSince]),
          note: (r[cNote] && cNote !== cSince ? r[cNote].toString().trim() : ''),
          status: looksSpare ? (looksBroken ? 'repair' : 'spare') : 'assigned',
          login: res.login,
          how: res.how,
          include: true,
        });
      }
      if (!out.length) throw new Error('No scanner rows read from that sheet.');
      setRows(out);
      setOrphans(stray.filter(Boolean));
      setNoScanner(blank.filter(Boolean));
    } catch (e) {
      setParseErr(e.message);
    }
  };

  // Two rows claiming the same asset number is the spreadsheet's signature failure —
  // the register can't hold it (one asset, one holder), so it must be resolved here.
  const dupAssets = useMemo(() => {
    if (!rows) return new Set();
    const seen = new Map();
    rows.filter((r) => r.include).forEach((r) => seen.set(r.asset, (seen.get(r.asset) || 0) + 1));
    return new Set([...seen.entries()].filter(([, n]) => n > 1).map(([a]) => a));
  }, [rows]);

  const unresolved = rows ? rows.filter((r) => r.include && r.status === 'assigned' && !r.login) : [];
  const conflicts = rows ? rows.filter((r) => {
    if (!r.include || r.status !== 'assigned' || !r.login) return false;
    const rec = (dms[r.login] || '').trim();
    return rec && rec !== r.asset;
  }) : [];
  const ready = rows && rows.some((r) => r.include) && unresolved.length === 0 && dupAssets.size === 0;

  const patch = (key, up) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...up } : r)));

  const commit = () => {
    const payload = rows.filter((r) => r.include).map((r) => ({
      asset: r.asset, model: r.model, status: r.status,
      holder: r.status === 'assigned' ? r.login : '',
      since: r.since, note: r.note,
      reason: r.status === 'repair' ? (r.note || 'marked faulty on the imported sheet') : '',
    }));
    act({ action: 'import', rows: payload, mode }, `Imported ${payload.length} scanners from ${sheetName}.`)
      .then(() => { setRows(null); onDone(); })
      .catch(() => {});
  };

  return (
    <div>
      <div style={{ ...card, marginBottom: 16 }}>
        <h2 style={{ fontSize: '1rem', margin: '0 0 6px' }}>Load a scanner spreadsheet</h2>
        <p style={{ margin: '0 0 10px', fontSize: '0.85rem', color: 'var(--hp-dim)' }}>
          Drop <code>Scanner Information.xlsx</code> (or any sheet with a <em>Scanner Number</em> column). Nothing is saved
          until you&rsquo;ve looked at what it found — logins are matched against the app&rsquo;s driver list, and rows the
          Documents page disagrees with are flagged.
        </p>
        <div
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) parse(f); }}
          onClick={() => fileRef.current && fileRef.current.click()}
          style={{ border: '2px dashed var(--hp-border)', borderRadius: 10, padding: '22px 16px', textAlign: 'center', cursor: 'pointer', color: 'var(--hp-dim)', fontSize: '0.9rem' }}
        >
          Drop the .xlsx here, or click to choose
          <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) parse(f); e.target.value = ''; }} />
        </div>
        {parseErr && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: 0 }}>{parseErr}</p>}
      </div>

      {rows && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
            <strong style={{ fontSize: '0.9rem' }}>{sheetName}</strong>
            <span style={{ color: 'var(--hp-dim)', fontSize: '0.85rem' }}>{rows.filter((r) => r.include).length} rows to import</span>
            <select style={field} value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="merge">Merge — update these, leave the rest of the register alone</option>
              <option value="replace">Replace — this sheet becomes the whole register</option>
            </select>
            <button style={btnPrimary} disabled={busy || !ready} onClick={commit}>Import</button>
          </div>

          {!ready && (
            <p style={{ fontSize: '0.85rem', color: '#b45309', margin: '0 0 12px' }}>
              {unresolved.length > 0 && <>{unresolved.length} row{unresolved.length === 1 ? '' : 's'} need a driver picked. </>}
              {dupAssets.size > 0 && <>{dupAssets.size} asset number{dupAssets.size === 1 ? ' is' : 's are'} on two rows — untick one or change it.</>}
            </p>
          )}
          {conflicts.length > 0 && (
            <div style={{ ...card, marginBottom: 12, fontSize: '0.85rem' }}>
              <strong>{conflicts.length} disagree with the Documents page.</strong> The sheet wins unless you untick the row —
              importing will overwrite the Documents record with the sheet&rsquo;s number.
            </div>
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}></th><th style={th}>Sheet name</th><th style={th}>Driver</th><th style={th}>Asset</th>
              <th style={th}>Model</th><th style={th}>Started</th><th style={th}>Status</th><th style={th}>Notes</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => {
                const rec = r.login ? (dms[r.login] || '').trim() : '';
                const conflict = r.status === 'assigned' && rec && rec !== r.asset;
                const dup = dupAssets.has(r.asset) && r.include;
                const known = existing.get(r.asset);
                const changes = known && (known.status !== r.status || (known.holder || '') !== (r.status === 'assigned' ? r.login : ''));
                return (
                  <tr key={r.key} style={{ opacity: r.include ? 1 : 0.45, background: dup ? 'rgba(239,68,68,0.06)' : undefined }}>
                    <td style={cell}><input type="checkbox" checked={r.include} onChange={(e) => patch(r.key, { include: e.target.checked })} /></td>
                    <td style={{ ...cell, fontSize: '0.8rem' }}>
                      {r.sheetName}
                      <div style={{ ...mono, color: 'var(--hp-dim)', fontSize: '0.72rem' }}>{r.sheetLogin || '—'}</div>
                    </td>
                    <td style={cell}>
                      {r.status === 'assigned' ? (
                        <>
                          <select style={{ ...field, padding: '4px 6px', fontSize: '0.8rem', maxWidth: 190 }}
                            value={r.login} onChange={(e) => patch(r.key, { login: e.target.value, how: 'manual' })}>
                            <option value="">— pick a driver —</option>
                            {drivers.map((d) => <option key={d.login} value={d.login}>{d.name || d.login} ({d.login}){d.active ? '' : ' · inactive'}</option>)}
                          </select>
                          {r.how === 'near' && <div><Warn>matched {r.sheetLogin} → {r.login}</Warn></div>}
                          {r.how === 'name' && <div><Warn>matched on name</Warn></div>}
                          {r.how === 'inactive' && <div><Warn tone="bad">only an inactive login fits — check it</Warn></div>}
                          {r.how === 'none' && <div><Warn tone="bad">no match — pick one</Warn></div>}
                        </>
                      ) : <span style={{ color: 'var(--hp-dim)' }}>—</span>}
                    </td>
                    <td style={{ ...cell, ...mono }}>
                      {r.asset}
                      {dup && <div><Warn tone="bad">on two rows</Warn></div>}
                      {conflict && <div><Warn>Documents: {rec}</Warn></div>}
                    </td>
                    <td style={cell}><input style={{ ...field, padding: '4px 6px', fontSize: '0.8rem', width: 80 }} value={r.model} onChange={(e) => patch(r.key, { model: e.target.value })} /></td>
                    <td style={cell}><input type="date" style={{ ...field, padding: '4px 6px', fontSize: '0.78rem' }} value={r.since} onChange={(e) => patch(r.key, { since: e.target.value })} /></td>
                    <td style={cell}>
                      <select style={{ ...field, padding: '4px 6px', fontSize: '0.8rem' }} value={r.status} onChange={(e) => patch(r.key, { status: e.target.value })}>
                        <option value="assigned">With driver</option><option value="spare">Spare</option>
                        <option value="repair">At repair</option><option value="retired">Retired</option>
                      </select>
                    </td>
                    <td style={{ ...cell, fontSize: '0.78rem', color: 'var(--hp-dim)', whiteSpace: 'normal', maxWidth: 220 }}>
                      {r.note}
                      {known && !changes && <div>already in the register, unchanged</div>}
                      {known && changes && <div style={{ color: '#b45309' }}>changes what the register says</div>}
                      {!known && <div>new to the register</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {noScanner.length > 0 && (
            <div style={{ ...card, marginTop: 16 }}>
              <strong style={{ fontSize: '0.9rem' }}>Listed with no scanner ({noScanner.length})</strong>
              <p style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', margin: '4px 0 0' }}>
                {noScanner.join(' · ')} — nothing to import for them. They&rsquo;ll show as &ldquo;No scanner&rdquo; on the Drivers tab.
              </p>
            </div>
          )}
          {orphans.length > 0 && (
            <div style={{ ...card, marginTop: 16 }}>
              <strong style={{ fontSize: '0.9rem' }}>Lines that aren&rsquo;t rows ({orphans.length})</strong>
              <p style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', margin: '4px 0 8px' }}>
                Free text at the bottom of the sheet. These usually describe a swap that never made it into the table —
                action them here with Swap, then they stop mattering.
              </p>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.82rem', color: 'var(--hp-dim)' }}>
                {orphans.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── little bits ───────────────────────────────────────────────────────────────
function Row({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}
function Modal({ title, onClose, children }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 16px', zIndex: 60, overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 12, padding: 20, width: '100%', maxWidth: 520 }}>
        <h2 style={{ margin: '0 0 14px', fontSize: '1.05rem' }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}
